<template>
  <SideBarSection
    :title="$t('shafts.dashboard.warnings')"
    icon="exclamation_triangle"
    :iconAndTitle="allWarningsActive"
  >
    <template #customEvent>
      <div
        v-if="!hasMaintenance && !hasOverdueJobs"
        :class="{
          'sidebar-item__section-link': true,
          'sidebar-item__section-link--back': allWarningsActive,
        }"
        @click="handleSeeAllWarnings"
      >
        {{ allWarningsActive ? $t('shafts.dashboard.back_to_warning_summary') : linkLabel }}
        <UseSvg :name="iconDirection" class="icon icon--small" />
      </div>
    </template>
    <!-- Sidebar slot content -->
    <div v-if="!loading">
      <div class="sidebar-item__section-sub-title typo-smallish">
        <div class="sidebar-item__warning-nav">
          <div
            :class="{
              'sidebar-item__warning-nav__link typo-small': true,
              'sidebar-item__warning-nav__link--active': currentWarningsDisplayed === 'overdue',
            }"
            @click="selectWarningType('overdue')"
          >
            {{ $t('shafts.dashboard.overdue_jobs') }}
          </div>
          <div
            :class="{
              'sidebar-item__warning-nav__link typo-small': true,
              'sidebar-item__warning-nav__link--active':
                currentWarningsDisplayed === 'maintenance',
            }"
            @click="selectWarningType('maintenance')"
          >
            {{ $t('shafts.dashboard.maintenance_required') }}
          </div>
        </div>
      </div>
      <template v-if="currentWarningsDisplayed === 'overdue'">
        <OverdueWarningCard :overdueJobs="warningData.overdue_jobs" />
        <SidebarEmptyState
          v-if="hasOverdueJobs"
          :title="$t('empty_states.warnings.overdue_jobs.title')"
          :message="$t('empty_states.warnings.overdue_jobs.message')"
        />
      </template>

      <div v-if="currentWarningsDisplayed === 'maintenance'">
        <MaintenanceWarningCard :data="warningData.maintenance_required.station_elements" typeStation="true" />
        <MaintenanceWarningCard :data="warningData.maintenance_required.shaft_elements" />
        <SidebarEmptyState
          v-if="hasMaintenance"
          :title="$t('empty_states.warnings.maintenance_required.title')"
          :message="$t('empty_states.warnings.maintenance_required.message')"
        />
      </div>
    </div>
    <Loader v-if="loading" class="sidebar-item__section-loader" />
  </SideBarSection>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import useAjax from '@/functions/use_ajax';
import UseSvg from '@/components/generic/use_svg.vue';
import I18n from 'i18n-js';

import Loader from '@/components/generic/loader.vue';
import SidebarEmptyState from '@/components/dashboard/sidebar/sidebar_section_empty_state.vue';
import SideBarSection from '@/components/dashboard/sidebar/sidebar_section.vue';
import OverdueWarningCard from '@/components/shafts/dashboard/warnings/overdue_warning_card.vue';
import MaintenanceWarningCard from '@/components/shafts/dashboard/warnings/maintenance_warning_card.vue';

const emit = defineEmits(['seeAllWarnings']);
const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
});

const route = useRoute();
const ajax = useAjax();
const warnings = ref(null);
const warningsDataLimited = ref(null);
const allWarningsActive = ref(false);

const loading = ref(false);
const currentWarningsDisplayed = ref(null);

const linkLabel = computed(() => {
  return I18n.t('general.see_all');
});

const shaftUrl = computed(() => {
  if (route.name.includes('shaftV2')) {
    return 'shafts_v2/shafts';
  } else {
    return 'shafts';
  }
});

/**
 * Returns all or limited warning data
 */
const warningData = computed(() =>
  allWarningsActive.value ? warnings.value : warningsDataLimited.value
);

/**
 * Checks if there is any maintenance
 */
const hasMaintenance = computed(() => {
  return (
    warningData.value &&
    warningData.value.maintenance_required.station_elements.length === 0 &&
    warningData.value.maintenance_required.shaft_elements.length === 0
  );
});

/**
 * Checks if there are any overdue jobs
 */
const hasOverdueJobs = computed(() => {
  return  warningData.value && warningData.value.overdue_jobs.length === 0;
});

/**
 * Flips the icon when see all is active
 */
const iconDirection = computed(() =>
  allWarningsActive.value ? 'arrow_left_16' : 'arrow_right_16'
);

onMounted(() => {
  loadWarnings();
});

/**
 * Watch for change on shaftID and get warnings
 */
watch(() => props.shaftId, loadWarnings);

/**
 * Get shaft warnings
 */
async function loadWarnings() {
  loading.value = true;

  let { ok, data } = await ajax(
    `/organisations/${props.organisationId}/${shaftUrl.value}/${props.shaftId}/dashboard/warnings`
  );

  if (ok) {
    warnings.value = data;

    warningsDataLimited.value = {
      overdue_jobs: data.overdue_jobs.slice(0, 5),
      maintenance_required: {
        station_elements: data.maintenance_required.station_elements.slice(0,1),
        shaft_elements: data.maintenance_required.shaft_elements.slice(0,1),
      },
    };

    currentWarningsDisplayed.value = 'overdue';
  }

  loading.value = false;
}

/**
 * Sets active class on warning tabs
 */
function selectWarningType(type) {
  currentWarningsDisplayed.value = type;
}

/**
 * Displays all warnings and emits event to parent (Dashboard)
 *  to hide other sidebar panels
 */
function handleSeeAllWarnings() {
  allWarningsActive.value = !allWarningsActive.value;
  emit('seeAllWarnings', allWarningsActive.value);
}
</script>
